import type { RecordRow } from '../../../data/client';
import { FieldDisplay } from '../../../fields/display/FieldDisplay';
import { FieldProvider } from '../../../fields/FieldContext';
import { cx, Tooltip } from '../../../ui';
import { displayContext, type TableColumn } from './tableModel';

/**
 * ONE CELL, AND IT IS A VALUE RATHER THAN A CONTROL (wave 3, L-B1; owner 2026-09-10, verbatim
 * "Ja" to Apple's selection model).
 *
 * WHAT WAS HERE AND WHY IT IS GONE. Every cell was its own editor: the `<td>` was a tab stop
 * with `onClick`, it opened `ui/CellEditor` - a portalled panel with a chevron box - and it
 * carried `cursor: text` so a pointer over a row said "select this string". The owner read all
 * three as defects in one look: "Komisch dass sich rechts auf oeffnet und gleichzeitig diese
 * view?" (the pane opened AND an editor opened), "Wenn ich rueber hovere auf alles ausser den
 * namen in einer Zeile Kriege ich einen anderen mauszeiger ... Das ist buggy", and of the
 * chevron "Was soll das symbol hier eig macht ja sinn aber ist das so apple like?".
 *
 * APPLE'S ANSWER IS THAT A TABLE ROW IS SELECTED, NOT EDITED. `NSTableView` sends its action
 * on a click and its DOUBLE action on a double click
 * (doc: https://developer.apple.com/documentation/appkit/nstableview/doubleaction), and the HIG
 * draws a list's state as a highlight rather than a ring
 * (hig: https://developer.apple.com/design/human-interface-guidelines/lists-and-tables). So the
 * first click selects the row, the pane beside it shows the record, and the record is where a
 * value is edited - in the pane's own `RecordDetails` group boxes or on the record page, both of
 * which already existed and are the SAME components (round 3, R3-1). Nothing was lost; one of
 * the two ways to edit a value was.
 *
 * WHAT THE CELL STILL DOES: it renders the value, it truncates, and it lets the row underneath
 * it hear the click (`RecordTableRow`'s capture handler), which is what makes "click any cell,
 * then act on the row" one gesture. It has no state, no ref and no effect.
 */
export interface RecordTableCellProps {
  readonly objectId: string;
  readonly record: RecordRow;
  readonly column: TableColumn;
  /** Titles the row, so the cell can say WHICH record it belongs to. */
  readonly rowTitle: string;
  /**
   * THE VALUE'S FULL TEXT WHEN IT DOES NOT FIT ITS COLUMN (R6 A9, S2), measured by the row
   * (`useClippedText`); absent when the value is readable as it stands.
   */
  readonly clippedText?: string | undefined;
}

export function RecordTableCell({
  objectId,
  record,
  column,
  rowTitle,
  clippedText,
}: RecordTableCellProps) {
  const { field } = column;
  const alignRight = column.align === 'right';

  const value = (
    <span
      data-cell-value={field.id}
      className={cx('min-w-0 flex-1 truncate', alignRight && 'text-right')}
    >
      <FieldProvider
        value={{
          ...displayContext(objectId, record, field),
          /**
           * P1-8: in a table cell the value is not the link. The jump is a small icon at
           * the trailing edge - a control with its own hit region, which is why it
           * survives L-B1: the finding was that the ROW was a link and therefore
           * answered Cmd and Shift with Chrome's own behaviours, and a deliberate 16px
           * affordance beside a value is the opposite of that.
           */
          linkAsIcon: true,
          /**
           * P4 round 2, finding F1: a SELECT value in this cell is TEXT WITH A COLOUR
           * DOT rather than a tinted capsule, which is what Finder draws beside a file
           * name. `tagAsDot` is the record's flag too (P5 round 3), so the table, the
           * phone list and a record's own fields cannot drift into three languages for
           * one value; `listRow` is the other half, the one that keeps an anchor plain.
           */
          tagAsDot: true,
          listRow: true,
        }}
      >
        <FieldDisplay />
      </FieldProvider>
    </span>
  );

  return (
    <td
      /**
       * THE NAME STAYS, THE TAB STOP GOES. A `cell` role takes an accessible name, and
       * "Status of Lena Roth" is what a screen reader and a test both reach for; what a
       * plain data cell must not be is a tab stop, because there is nothing to activate on
       * it any more and the table's own scroll region is the keyboard's landing place
       * (`TableSurface`, spec/screens/list.md section 5).
       */
      aria-label={`${field.label} of ${rowTitle}`}
      /**
       * `cursor: default` IS THE FINDING (L-B1, and the product-wide rule SH-A13): a row is
       * something you point at, so it shows the arrow. It used to be `cursor-text` on every
       * cell but the archived ones, which is the I-beam the owner met.
       */
      className="data-cell cursor-default border-b border-line-subtle align-middle text-ink"
    >
      <div className={cx('flex items-center gap-1.5', alignRight && 'justify-end')}>
        {clippedText === undefined ? value : <Tooltip label={clippedText}>{value}</Tooltip>}
      </div>
    </td>
  );
}
